import { ReducerCreators } from '@reduxjs/toolkit';

import { ProjectState } from '../types';
import loadProjectReducer from './load-project-reducer';
import loadPreviewTemplateReducer from './load-preview-template-reducer';
import getPublishedEventUrlReducer from './get-published-event-url-reducer';
import clearPublishedEventUrlReducer from './clear-published-event-url-reducer';
import setCreateProjectErrorFieldsReducer from './set-create-project-error-fields-reducer';
import removeCreateProjectErrorFieldsReducer from './remove-create-project-error-fields-reducer';
import setWorkingProjectReducer from './set-working-project-reducer';
import saveWorkingProjectReducer from './save-working-project-reducer';
import saveWorkingProjectByFieldsReducer from './save-working-project-by-fields-reducer';
import clearSaveSessionAfterProjectSaveReducer from './clear-save-session-after-project-save-reducer';
import setSavingWorkingProjectReducer from './set-saving-working-project-reducer';
import setSavingWorkingProjectErrorReducer from './set-saving-working-project-error-reducer';
import saveWorkingProjectSessionReducer from './save-working-project-session-reducer';
import saveWorkingProjectSessionByFieldReducer from './save-working-project-session-by-field-reducer';
import setUpdatingSessionUuidReducer from './set-updating-session-uuid-reducer';
import loadWorkingProjectWithIndexedDBReducer from './load-working-project-with-indexed-db-reducer';
import updateWorkingProjectWithIndexedDbReducer from './update-working-project-with-indexed-db-reducer';
import deleteSessionReducer from './delete-session-reducer';
import clearPreviewTemplateReducer from './clear-preview-template-reducer';
import loadProjectGroupsReducer from './load-project-groups-reducer';
import setupProjectPreviewReducer from './setup-project-preview-reducer';
import updateProjectSettingsReducer from './update-project-settings-reducer';
import setRegistrationOnReducer from './set-registration-on-reducer';
import updateHomepageHeaderReducer from './update-homepage-header-reducer';
import updateHomepageModuleReducer from './update-homepage-module-reducer';
import addLeaderboardNavItemReducer from './add-leaderboard-nav-item-reducer';
import updateRegistrationStepsReducer from './update-registration-steps-reducer';
import setActivePreviewPageTabReducer from './set-active-preview-page-tab-reducer';
import setDisplayEditProjectThemeTrayReducer from './set-display-edit-project-theme-tray-reducer';
import setDisplayPreviewTemplateThemeTrayReducer from './set-display-preview-template-theme-tray-reducer';
import setShowUpdateProjectTypeModalReducer from './set-show-update-project-type-modal-reducer';
import publishWorkingProjectReducer from './publish-working-project-reducer';
import editScheduledEmailReducer from './edit-scheduled-email-reducer';
import getProjectScheduledEmailsReducer from './get-project-scheduled-emails-reducer';
import setSelectEditOrPublishButtonStateReducer from './set-select-edit-or-publish-button-state-reducer';
import setShowUneditedContentReducer from './set-show-unedited-content-reducer';
import setPreviewTemplateOverridesReducer from './set-preview-template-overrides-reducer';
import setIsCreatingNewSessionReducer from './set-is-creating-new-session-reducer';
import duplicateSessionReducer from './duplicate-session-reducer';
import setWorkingProjectThemeReducer from './set-working-project-theme-reducer';
import updateProjectPreviewPasswordReducer from './update-project-preview-password-reducer';
import loadMostRecentActivityLogReducer from './load-most-recent-activity-log-reducer';
import updateMostRecentActivityReducer from './update-most-recent-activity-reducer';
import loadGreenroomMeetingReducer from './load-greenroom-meeting-reducer';
import loadGreenroomDataReducer from './load-greenroom-data-reducer';
import duplicateProjectReducer from './duplicate-project-reducer';
import loadWorkingSessionReducer from './load-working-session-reducer';
import updatePublishSettingsReducer from './update-publish-settings-reducer';
import setFaviconReducer from './set-favicon-reducer';
import fetchGreenroomMeetingReducer from './fetch-greenroom-meeting-reducer';
import updateSessionCrewAssociationsReducer from './update-session-crew-associations-reducer';
import updateMeetingInviteSliceReducer from './update-meeting-invite-slice-reducer';
import revokeGreenroomInvitationReducer from './revoke-greenroom-invitation-reducer';
import fetchSessionEngagementTotalsReducer from './fetch-session-engagement-totals-reducer';
import setShowAnnouncementsModalReducer from './set-show-announcements-modal-reducer';
import updateCrewAdminChannelsReducer from './update-crew-admin-channels-reducer';
import setStreamingOptionOnAllSessionsReducer from './set-streaming-option-on-all-sessions-reducer';
import saveFieldOnAllSessionsReducer from './save-field-on-all-sessions-reducer';

export const reducers = (create: ReducerCreators<ProjectState>) => ({
  loadProject: loadProjectReducer(create),
  loadPreviewTemplate: loadPreviewTemplateReducer(create),
  getPublishedEventUrl: getPublishedEventUrlReducer(create),
  clearPublishedEventUrl: clearPublishedEventUrlReducer(create),
  setCreateProjectErrorFields: setCreateProjectErrorFieldsReducer(create),
  removeCreateProjectErrorFields: removeCreateProjectErrorFieldsReducer(create),
  setWorkingProject: setWorkingProjectReducer(create),
  /**
   * saveWorkingProject triggers save-project-middleware.ts
   *
   */
  saveWorkingProject: saveWorkingProjectReducer(create),
  /**
   * Why by fields?
   *
   * So we don't have to import the whole project into each component which causes unnecessary re-renders.
   *
   * saveWorkingProjectByFields triggers save-project-by-fields-middleware.ts
   *
   * ************
   * ************
   * IMPORTANT: This only works on top level fields of the project object.
   * You can still use this to update nested fields, but you need to pass the entire object into the payload.
   * ************
   * ************
   */
  saveWorkingProjectByFields: saveWorkingProjectByFieldsReducer(create),
  clearSaveSessionAfterProjectSave: clearSaveSessionAfterProjectSaveReducer(create),
  setSavingWorkingProject: setSavingWorkingProjectReducer(create),
  setSavingWorkingProjectError: setSavingWorkingProjectErrorReducer(create),
  saveWorkingProjectSession: saveWorkingProjectSessionReducer(create),
  saveWorkingProjectSessionByField: saveWorkingProjectSessionByFieldReducer(create),
  setUpdatingSessionUuid: setUpdatingSessionUuidReducer(create),
  loadWorkingProjectWithIndexedDB: loadWorkingProjectWithIndexedDBReducer(create),
  updateWorkingProjectWithIndexedDb: updateWorkingProjectWithIndexedDbReducer(create),
  deleteSession: deleteSessionReducer(create),
  clearPreviewTemplate: clearPreviewTemplateReducer(create),
  loadProjectGroups: loadProjectGroupsReducer(create),
  setupProjectPreview: setupProjectPreviewReducer(create),
  updateProjectSettings: updateProjectSettingsReducer(create),
  setRegistrationOn: setRegistrationOnReducer(create),
  updateHomepageHeader: updateHomepageHeaderReducer(create),
  updateHomepageModule: updateHomepageModuleReducer(create),
  addLeaderboardNavItem: addLeaderboardNavItemReducer(create),
  updateRegistrationSteps: updateRegistrationStepsReducer(create),
  setActivePreviewPageTab: setActivePreviewPageTabReducer(create),
  setDisplayEditProjectThemeTray: setDisplayEditProjectThemeTrayReducer(create),
  setDisplayPreviewTemplateThemeTray: setDisplayPreviewTemplateThemeTrayReducer(create),
  setShowUpdateProjectTypeModal: setShowUpdateProjectTypeModalReducer(create),
  publishWorkingProject: publishWorkingProjectReducer(create),
  editScheduledEmail: editScheduledEmailReducer(create),
  getProjectScheduledEmails: getProjectScheduledEmailsReducer(create),
  setSelectEditOrPublishButtonState: setSelectEditOrPublishButtonStateReducer(create),
  setShowUneditedContent: setShowUneditedContentReducer(create),
  setPreviewTemplateOverrides: setPreviewTemplateOverridesReducer(create),
  setIsCreatingNewSession: setIsCreatingNewSessionReducer(create),
  duplicateSession: duplicateSessionReducer(create),
  setWorkingProjectTheme: setWorkingProjectThemeReducer(create),
  updateProjectPreviewPassword: updateProjectPreviewPasswordReducer(create),
  loadMostRecentActivityLog: loadMostRecentActivityLogReducer(create),
  updateMostRecentActivity: updateMostRecentActivityReducer(create),
  loadGreenroomMeeting: loadGreenroomMeetingReducer(create),
  loadGreenroomData: loadGreenroomDataReducer(create),
  duplicateProject: duplicateProjectReducer(create),
  loadWorkingSession: loadWorkingSessionReducer(create),
  updatePublishSettings: updatePublishSettingsReducer(create),
  setFavicon: setFaviconReducer(create),
  fetchGreenroomMeeting: fetchGreenroomMeetingReducer(create),
  updateSessionCrewAssociations: updateSessionCrewAssociationsReducer(create),
  updateMeetingInviteSlice: updateMeetingInviteSliceReducer(create),
  revokeGreenroomInvitation: revokeGreenroomInvitationReducer(create),
  fetchSessionEngagementTotals: fetchSessionEngagementTotalsReducer(create),
  setShowAnnouncementsModal: setShowAnnouncementsModalReducer(create),
  updateCrewAdminChannels: updateCrewAdminChannelsReducer(create),
  setStreamingOptionOnAllSessions: setStreamingOptionOnAllSessionsReducer(create),
  saveFieldOnAllSessions: saveFieldOnAllSessionsReducer(create),
});
